// src/App.js — Main Router (Fertilizer portal + Fleet GPS portal)
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './store/AuthContext';
import type { UserRole } from './types';
import './index.css';

// Public
import LandingPage     from './landing/LandingPage';
import LoginPage       from './pages/LoginPage';
import FarmerScanPage  from './pages/FarmerScanPage';

// Fertilizer portal
import DashboardPage   from './pages/DashboardPage';
import UploadPage      from './pages/UploadPage';
import BagsPage        from './pages/BagsPage';
import OperatorPage    from './pages/OperatorPage';
import DealerPage      from './pages/DealerPage';
import DealersPage     from './pages/DealersPage';
import AlertsPage      from './pages/AlertsPage';
import QRPrintPage     from './pages/QRPrintPage';
import ReportsPage     from './pages/ReportsPage';
import AppShell        from './components/layout/AppShell';

// Fleet portal
import FleetLoginPage      from './pages/fleet/FleetLoginPage';
import FleetAppShell       from './components/layout/FleetAppShell';
import FleetDashboardPage  from './pages/fleet/FleetDashboardPage';
import TripListPage        from './pages/fleet/TripListPage';
import CreateTripPage      from './pages/fleet/CreateTripPage';
import TripDetailPage      from './pages/fleet/TripDetailPage';
import FleetAlertsPage     from './pages/fleet/FleetAlertsPage';
import FleetReportsPage    from './pages/fleet/FleetReportsPage';
import AssetMasterPage     from './pages/fleet/master/AssetMasterPage';
import VehicleMasterPage   from './pages/fleet/master/VehicleMasterPage';
import VendorMasterPage    from './pages/fleet/master/VendorMasterPage';
import StateMasterPage     from './pages/fleet/master/StateMasterPage';
import DistrictMasterPage  from './pages/fleet/master/DistrictMasterPage';
import IfmsQueuePage       from './pages/fleet/IfmsQueuePage';
import RolesPage           from './pages/fleet/admin/RolesPage';
import {
  TripReportPage, TripPerformancePage, VehicleSummaryPage, StoppageReportPage, OverspeedReportPage, AlertReportPage, DeviceHealthPage,
} from './pages/fleet/reports/ReportPages';
import UsersPage           from './pages/fleet/admin/UsersPage';
import IfmsSettingsPage    from './pages/fleet/admin/IfmsSettingsPage';
import FleetHelpdeskPage   from './pages/fleet/FleetHelpdeskPage';
import GpsProvidersPage    from './pages/fleet/admin/GpsProvidersPage';
import { can, fleetHome }  from './pages/fleet/permissions';
import type { FleetPermission } from './types';

// Route guard — loginPath lets each portal send guests to its own login.
interface PrivateRouteProps {
  children: React.ReactElement;
  roles?: UserRole[];
  loginPath?: string;
  home?: string;
}

function PrivateRoute({ children, roles, loginPath = '/login', home = '/dashboard' }: PrivateRouteProps) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spin">⟳</div></div>;
  if (!user)   return <Navigate to={loginPath} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={home} replace />;
  return children;
}

const STAFF_ROLES: UserRole[] = ['SUPER_ADMIN', 'GOVT_OFFICER'];
const FLEET_ROLES: UserRole[] = ['SUPER_ADMIN', 'GOVT_OFFICER', 'FLEET_USER'];

// Fleet page guard — a page the user's roles do not grant sends them to one they can open.
function RequirePerm({ perm, children }: { perm: FleetPermission; children: React.ReactElement }) {
  const { user } = useAuth();
  if (can(user, perm)) return children;
  const home = fleetHome(user);
  return home ? <Navigate to={home} replace /> : <div style={{ padding: 40, fontSize: 14 }}>Your account has no Fleet access yet. Ask an administrator to assign a role.</div>;
}
const need = (perm: FleetPermission, el: React.ReactElement) => <RequirePerm perm={perm}>{el}</RequirePerm>;

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/"             element={<LandingPage />} />
          <Route path="/login"        element={<LoginPage />} />
          <Route path="/fleet/login"  element={<FleetLoginPage />} />
          <Route path="/scan/:bagId"  element={<FarmerScanPage />} />

          {/* Fertilizer portal (existing paths kept) */}
          <Route element={<PrivateRoute roles={STAFF_ROLES} home="/fleet/dashboard"><AppShell /></PrivateRoute>}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/upload"    element={<PrivateRoute roles={['SUPER_ADMIN','GOVT_OFFICER']}><UploadPage /></PrivateRoute>} />
            <Route path="/bags"      element={<BagsPage />} />
            <Route path="/operator"  element={<PrivateRoute roles={['SUPER_ADMIN','GOVT_OFFICER']}><OperatorPage /></PrivateRoute>} />
            <Route path="/dealer"    element={<PrivateRoute roles={['SUPER_ADMIN','GOVT_OFFICER']}><DealerPage /></PrivateRoute>} />
            <Route path="/dealers"   element={<PrivateRoute roles={['SUPER_ADMIN','GOVT_OFFICER']}><DealersPage /></PrivateRoute>} />
            <Route path="/alerts"    element={<AlertsPage />} />
            <Route path="/qr-print"  element={<PrivateRoute roles={['SUPER_ADMIN','GOVT_OFFICER']}><QRPrintPage /></PrivateRoute>} />
            <Route path="/reports"   element={<ReportsPage />} />
          </Route>

          {/* Fleet portal */}
          <Route element={<PrivateRoute roles={FLEET_ROLES} loginPath="/fleet/login" home="/fleet/dashboard"><FleetAppShell /></PrivateRoute>}>
            <Route path="/fleet/dashboard" element={need('dashboard', <FleetDashboardPage />)} />
            <Route path="/fleet/vehicles"  element={<Navigate to="/fleet/master/vehicles" replace />} />
            <Route path="/fleet/master/assets"    element={need('master', <AssetMasterPage />)} />
            <Route path="/fleet/master/vehicles"  element={need('master', <VehicleMasterPage />)} />
            <Route path="/fleet/master/vendors"   element={need('master', <VendorMasterPage />)} />
            <Route path="/fleet/master/states"    element={need('master', <StateMasterPage />)} />
            <Route path="/fleet/master/districts" element={need('master', <DistrictMasterPage />)} />
            <Route path="/fleet/trips/new" element={need('create_trip', <CreateTripPage />)} />
            <Route path="/fleet/trips/:id/edit" element={need('create_trip', <CreateTripPage />)} />
            <Route path="/fleet/trips/:id" element={need('trip_list', <TripDetailPage />)} />
            <Route path="/fleet/trips"     element={need('trip_list', <TripListPage />)} />
            <Route path="/fleet/ifms"      element={need('ifms', <IfmsQueuePage />)} />
            <Route path="/fleet/alerts"    element={need('alerts', <FleetAlertsPage />)} />
            <Route path="/fleet/reports"   element={need('reports', <FleetReportsPage />)} />
            <Route path="/fleet/reports/trips"     element={need('reports', <TripReportPage />)} />
            <Route path="/fleet/reports/performance" element={need('reports', <TripPerformancePage />)} />
            <Route path="/fleet/reports/vehicles"  element={need('reports', <VehicleSummaryPage />)} />
            <Route path="/fleet/reports/stoppages" element={need('reports', <StoppageReportPage />)} />
            <Route path="/fleet/reports/overspeed" element={need('reports', <OverspeedReportPage />)} />
            <Route path="/fleet/reports/alerts"    element={need('reports', <AlertReportPage />)} />
            <Route path="/fleet/reports/devices"   element={need('reports', <DeviceHealthPage />)} />
            <Route path="/fleet/admin/roles" element={need('admin', <RolesPage />)} />
            <Route path="/fleet/admin/users" element={need('admin', <UsersPage />)} />
            <Route path="/fleet/admin/ifms" element={need('ifms', <IfmsSettingsPage />)} />
            <Route path="/fleet/admin/providers" element={need('admin', <GpsProvidersPage />)} />
            {/* Contacts and downloads only — open to anyone signed in. */}
            <Route path="/fleet/helpdesk"  element={<FleetHelpdeskPage />} />
          </Route>

          {/* Unknown → landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
